import { useMemo, type ReactNode } from "react";
import type { CoachingNote } from "@/lib/types";
import {
  loadDrawing,
  type TelestratorObject,
} from "@/lib/canvas/telestrator-schema";
import { FeedbackMediaThumbnail } from "./FeedbackMediaThumbnail";

export type VideoNoteThumbnailProps = {
  note: Pick<CoachingNote, "id" | "drawing">;
  className: string;
  testId: string;
  placeholderTestId: string;
  overlayTestId?: string;
  imgClassName?: string;
};

/** Static marked-frame preview for a video note.
 *
 * A Mark Moment note is not a clip: it represents one saved frame
 * plus the coach's telestration drawing. The server thumbnail gives
 * us the access-controlled video frame; this component paints the
 * persisted drawing on top so every surface shows the same evidence.
 */
export function VideoNoteThumbnail({
  note,
  className,
  testId,
  placeholderTestId,
  overlayTestId = "video-note-drawing-overlay",
  imgClassName,
}: VideoNoteThumbnailProps) {
  const drawingObjects = useMemo(
    () => loadDrawing(note.drawing).objects,
    [note.drawing],
  );
  return (
    <FeedbackMediaThumbnail
      kind="note"
      id={note.id}
      testId={testId}
      placeholderTestId={placeholderTestId}
      className={className}
      imgClassName={imgClassName}
    >
      {drawingObjects.length > 0 ? (
        <ThumbnailDrawingOverlay
          overlayId={`note-${note.id}`}
          objects={drawingObjects}
          testId={overlayTestId}
        />
      ) : null}
    </FeedbackMediaThumbnail>
  );
}

const THUMBNAIL_COORD_SCALE = 100;

export function ThumbnailDrawingOverlay({
  overlayId,
  objects,
  testId,
}: {
  overlayId: string;
  objects: TelestratorObject[];
  testId: string;
}) {
  if (objects.length === 0) return null;
  const arrowId = `video-thumb-arrow-${overlayId}`;
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox={`0 0 ${THUMBNAIL_COORD_SCALE} ${THUMBNAIL_COORD_SCALE}`}
      preserveAspectRatio="none"
      data-testid={testId}
      aria-hidden="true"
    >
      <defs>
        <marker
          id={arrowId}
          markerWidth="6"
          markerHeight="6"
          refX="5"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill="context-stroke" />
        </marker>
      </defs>
      {objects.map((object, index) =>
        renderThumbnailDrawingObject(object, `${overlayId}-${index}`, arrowId),
      )}
    </svg>
  );
}

function renderThumbnailDrawingObject(
  object: TelestratorObject,
  key: string,
  arrowId: string,
): ReactNode {
  const stroke = "color" in object && object.color ? object.color : "#f43f5e";
  const rawStrokeWidth =
    "strokeWidth" in object && typeof object.strokeWidth === "number"
      ? object.strokeWidth
      : 3;
  const strokeWidth = Math.max(2, Math.min(5, rawStrokeWidth));
  const common = {
    stroke,
    strokeWidth,
    vectorEffect: "non-scaling-stroke" as const,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  switch (object.kind) {
    case "arrow":
    case "line":
      return (
        <line
          key={key}
          x1={scaleCoord(object.x1)}
          y1={scaleCoord(object.y1)}
          x2={scaleCoord(object.x2)}
          y2={scaleCoord(object.y2)}
          fill="none"
          markerEnd={object.kind === "arrow" ? `url(#${arrowId})` : undefined}
          {...common}
        />
      );
    case "freehand": {
      const points = pointsToSvg(object.points);
      if (!points) return null;
      return <polyline key={key} points={points} fill="none" {...common} />;
    }
    case "zone":
      return (
        <rect
          key={key}
          x={scaleCoord(object.x)}
          y={scaleCoord(object.y)}
          width={scaleCoord(object.w)}
          height={scaleCoord(object.h)}
          fill={stroke}
          fillOpacity={0.12}
          {...common}
        />
      );
    case "circle":
    case "spotlight":
      return (
        <ellipse
          key={key}
          cx={scaleCoord(object.x + object.w / 2)}
          cy={scaleCoord(object.y + object.h / 2)}
          rx={scaleCoord(object.w / 2)}
          ry={scaleCoord(object.h / 2)}
          fill="none"
          {...common}
        />
      );
    case "label":
      return (
        <g key={key}>
          <circle
            cx={scaleCoord(object.x)}
            cy={scaleCoord(object.y)}
            r="3"
            fill={stroke}
            stroke="#00122c"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
          <text
            x={scaleCoord(object.x) + 4}
            y={scaleCoord(object.y) + 2}
            fill="#ffffff"
            fontSize="7"
            fontWeight="700"
            paintOrder="stroke"
            stroke="#00122c"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          >
            {object.text.slice(0, 12)}
          </text>
        </g>
      );
    case "formation": {
      const hull = pointsToSvg(object.hull_points);
      return (
        <g key={key}>
          {hull ? (
            <polygon points={hull} fill={stroke} fillOpacity={0.1} {...common} />
          ) : null}
          {object.anchors.map((anchor, index) => (
            <circle
              key={`${key}-anchor-${index}`}
              cx={scaleCoord(anchor.x)}
              cy={scaleCoord(anchor.y)}
              r="2.5"
              fill={stroke}
              stroke="#00122c"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </g>
      );
    }
    case "dim":
      return (
        <rect
          key={key}
          x="0"
          y="0"
          width={THUMBNAIL_COORD_SCALE}
          height={THUMBNAIL_COORD_SCALE}
          fill="#00122c"
          opacity={Math.max(0.18, Math.min(0.45, object.opacity))}
        />
      );
    default:
      return null;
  }
}

function scaleCoord(value: number): number {
  return value * THUMBNAIL_COORD_SCALE;
}

function pointsToSvg(points: { x: number; y: number }[]): string | null {
  if (points.length < 2) return null;
  return points.map((point) => `${scaleCoord(point.x)},${scaleCoord(point.y)}`).join(" ");
}
